<template>
  <div
    :class="[
      'q-textarea',
      `q-textarea--${size}`,
      `q-textarea--${variant}`,
      {
        'q-textarea--error': error,
        'q-textarea--disabled': disabled,
        'q-textarea--readonly': readonly,
        'q-textarea--focused': focused,
        'q-textarea--hover': hover,
        'q-textarea--full-width': fullWidth,
      },
    ]"
  >
    <div class="q-textarea__wrapper">
      <textarea
        ref="textareaRef"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        class="q-textarea__input"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <div
        v-if="maxlength"
        class="q-textarea__counter"
      >
        {{ modelValue?.toString()?.length || 0 }} / {{ maxlength }}
      </div>
    </div>
    <div
      v-if="error"
      class="q-textarea__error"
    >
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'm',
    validator: (value) => ['l', 'm', 's'].includes(value),
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'tertiary'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  hover: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  maxlength: {
    type: Number,
    default: undefined,
  },
  autoResize: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur']);

const textareaRef = ref(null);
const focused = ref(false);

// Min height based on size
const minHeight = computed(() => {
  switch (props.size) {
    case 'l':
      return 160;
    case 's':
      return 96;
    case 'm':
    default:
      return 120;
  }
});

const setAutoHeight = () => {
  if (!props.autoResize || !textareaRef.value) return;
  textareaRef.value.style.height = 'auto';
  const base = minHeight.value;
  const scrollH = textareaRef.value.scrollHeight;
  textareaRef.value.style.height = Math.max(base, scrollH) + 'px';
};

const handleInput = (event) => {
  if (event && event.target) {
    emit('update:modelValue', event.target.value);
    nextTick(setAutoHeight);
  }
};

const handleFocus = (event) => {
  focused.value = true;
  emit('focus', event);
};

const handleBlur = (event) => {
  focused.value = false;
  emit('blur', event);
};

onMounted(() => {
  nextTick(setAutoHeight);
});
</script>

<style lang="scss" scoped>
.q-textarea__wrapper {
  overflow: hidden;
}
.q-textarea {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__wrapper {
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
    transition: all 0.2s ease;
  }

  &__input {
    width: 100%;
    border: none;
    outline: none;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    transition: all 0.2s ease;
    resize: none;
    background: transparent;
    color: rgba(14, 14, 34, 0.9);

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:readonly {
      cursor: default;
    }
  }

  &__counter {
    position: absolute;
    right: 12px;
    bottom: 8px;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.2;
    color: rgba(14, 14, 34, 0.4);
    pointer-events: none;
  }

  // Focused state — highlight counter
  &.q-textarea--focused {
    .q-textarea__counter {
      color: rgba(14, 14, 34, 0.7);
    }
  }

  // Large size
  &--l {
    .q-textarea__wrapper {
      border-radius: 16px;
    }

    .q-textarea__input {
      padding: 12px;
      font-size: 16px;
      line-height: 1.4;
      min-height: 160px;
    }
  }

  // Medium size
  &--m {
    .q-textarea__wrapper {
      border-radius: 12px;
    }

    .q-textarea__input {
      padding: 12px;
      font-size: 16px;
      line-height: 1.4;
      min-height: 120px;
    }
  }

  // Small size
  &--s {
    .q-textarea__wrapper {
      border-radius: 8px;
    }

    .q-textarea__input {
      padding: 12px;
      font-size: 14px;
      line-height: 1.4;
      min-height: 96px;
    }
  }

  // Primary variant
  &--primary {
    .q-textarea__wrapper {
      background-color: var(--color-white);
    }

    .q-textarea__input {
      color: rgba(14, 14, 34, 0.4);

      &::placeholder {
        color: rgba(14, 14, 34, 0.4);
      }
    }

    &:hover:not(.q-textarea--disabled) {
      .q-textarea__input {
        color: rgba(14, 14, 34, 0.6);
        &::placeholder {
          color: rgba(14, 14, 34, 0.6);
        }
      }
    }

    &.q-textarea--focused {
      .q-textarea__input {
        color: rgba(14, 14, 34, 0.9);
        &::placeholder {
          color: rgba(14, 14, 34, 0.9);
        }
      }
    }
  }

  // Secondary variant
  &--secondary {
    .q-textarea__wrapper {
      textarea {
        background-color: #f7f7f7 !important;
      }
    }

    .q-textarea__input {
      color: rgba(14, 14, 34, 0.4);

      &::placeholder {
        color: rgba(14, 14, 34, 0.4);
      }
    }

    &:hover:not(.q-textarea--disabled) {
      .q-textarea__input {
        color: rgba(14, 14, 34, 0.6);
        &::placeholder {
          color: rgba(14, 14, 34, 0.6);
        }
      }
    }

    &.q-textarea--focused {
      .q-textarea__input {
        color: rgba(14, 14, 34, 0.8);
        &::placeholder {
          color: rgba(14, 14, 34, 0.8);
        }
      }
    }
  }

  // Tertiary variant
  &--tertiary {
    .q-textarea__wrapper {
      background-color: transparent;
    }

    .q-textarea__input {
      color: rgba(14, 14, 34, 0.8);

      &::placeholder {
        color: rgba(14, 14, 34, 0.8);
      }
    }

    &:hover:not(.q-textarea--disabled) {
      .q-textarea__wrapper {
        background-color: rgba(14, 14, 34, 0.06);
      }
    }

    &.q-textarea--focused {
      .q-textarea__wrapper {
        background-color: rgba(14, 14, 34, 0.06);
      }
    }
  }

  // Error state
  &--error {
    .q-textarea__wrapper {
      border: 1px solid #f23f3f;
    }

    .q-textarea__input {
      color: #f23f3f;

      &::placeholder {
        color: #f23f3f;
      }
    }
  }

  // Disabled state
  &--disabled {
    .q-textarea__wrapper {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  // Readonly state
  &--readonly {
    .q-textarea__wrapper {
      cursor: default;
    }
  }

  // Full width modifier
  &--full-width {
    width: 100%;
    max-width: none;
  }

  &__error {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.2;
    color: #f23f3f;
    margin-left: 4px;
  }
}
</style>
